import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  XIcon,
  UploadIcon,
  LinkIcon,
  CalendarIcon,
  MapPinIcon,
  UserIcon,
  DollarSignIcon,
  FileTextIcon,
  AlertCircleIcon,
  ExternalLinkIcon,
} from 'lucide-react'
import { AddressAutocomplete } from './AddressAutocomplete'
import { NotesAndActivityPanel, PanelNote } from './NotesAndActivityPanel'
import { LeadCustomerSearchSelect } from './LeadCustomerSearchSelect'
import { PropertyProjectSearchSelect } from './PropertyProjectSearchSelect'
import { RequestSectionPanel } from './RequestSectionPanel'
import { selectionFromIds } from '../lib/leadCustomerSearch'
import type { ProjectItem, PropertyItem } from '../lib/propertyProjectSearch'
import {
  mapProjectTypeToWorkTypeId,
} from '../lib/workTypeMapping'
import { mapRequestProjectTypeToServiceType } from '../lib/mapProposalRequestToProposal'
import {
  SERVICE_TYPE_OPTIONS,
} from '../lib/serviceTypeOptions'
import { resolveOwnerContact, hasLinkedOwner } from '../lib/resolveOwnerContact'
import { BUILDING_TYPES } from './property/types'
import { supabase } from '../lib/supabase'
import { mobile } from './mobile'
import {
  ActivityLog,
  ActivityTypes,
  getEntityActivities,
  logActivity,
  logActivityOnLinkedParents,
} from '../utils/activityLogger'

interface Attachment {
  name: string
  url: string
  type: string
  size: number
  uploaded_at: string
}

interface BlueprintLink {
  title: string
  url: string
  description: string
}

interface ProposalNote {
  note: string
  timestamp: string
}

type ProposalRequestTab = 'overview' | 'details' | 'documents' | 'notes'

function parseRequestNotes(raw: unknown): ProposalNote[] {
  if (!raw) return []
  if (Array.isArray(raw)) return raw as ProposalNote[]
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  return []
}

const FIELD_TAB_MAP: Record<string, ProposalRequestTab> = {
  owner: 'overview',
  contact_name: 'overview',
  contact_email: 'overview',
  title: 'details',
  service_type: 'details',
  property_address: 'details',
  property_type: 'details',
}

const TAB_ORDER: ProposalRequestTab[] = ['overview', 'details', 'documents', 'notes']

function tabForFirstError(errors: Record<string, string>): ProposalRequestTab {
  for (const field of Object.keys(errors)) {
    const tab = FIELD_TAB_MAP[field]
    if (tab) return tab
  }
  return 'overview'
}

interface ProposalRequestFormProps {
  onClose: () => void
  onSuccess: () => void
  initialLeadId?: string
  initialCustomerId?: string
  editingRequest?: any // Request data when editing
  viewingRequest?: any // Request data when viewing
  isFullPage?: boolean // If true, renders as full page instead of modal
}

export function ProposalRequestForm({
  onClose,
  onSuccess,
  initialLeadId,
  initialCustomerId,
  editingRequest,
  viewingRequest,
  isFullPage = false,
}: ProposalRequestFormProps) {
  const isViewMode = !!viewingRequest && !editingRequest
  const isEditMode = !!editingRequest
  const requestData = editingRequest || viewingRequest

  const [leads, setLeads] = React.useState<any[]>([])
  const [customers, setCustomers] = React.useState<any[]>([])
  const [serviceCategories, setServiceCategories] = React.useState<
    Array<{ id: string; name: string }>
  >([])
  const [projects, setProjects] = React.useState<any[]>([])
  const [properties, setProperties] = React.useState<any[]>([])
  const [ownerType, setOwnerType] = React.useState<'lead' | 'customer'>(
    requestData?.lead_id ? 'lead' : requestData?.customer_id ? 'customer' : initialLeadId ? 'lead' : initialCustomerId ? 'customer' : 'lead'
  )

  const [notification, setNotification] = useState<{
    message: string
    type: 'success' | 'error'
  } | null>(null)

  const formatDateForInput = (dateString: string | null | undefined): string => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      // Format as YYYY-MM-DD for date input
      return date.toISOString().split('T')[0]
    } catch {
      return ''
    }
  }

  const [formData, setFormData] = useState({
    title: requestData?.title || '',
    description: requestData?.description || '',
    service_type:
      requestData?.service_type ||
      (requestData?.project_type &&
      SERVICE_TYPE_OPTIONS.some((o) => o.value === requestData.project_type)
        ? requestData.project_type
        : ''),
    project_type: requestData?.project_type || '',
    property_address: requestData?.property_address || '',
    property_city: requestData?.property_city || '',
    property_state: requestData?.property_state || '',
    property_zip: requestData?.property_zip || '',
    property_type: requestData?.property_type || '',
    square_footage: requestData?.square_footage?.toString() || '',
    estimated_budget: requestData?.estimated_budget?.toString() || '',
    preferred_start_date: formatDateForInput(requestData?.preferred_start_date),
    preferred_completion_date: formatDateForInput(requestData?.preferred_completion_date),
    urgency_level: requestData?.urgency_level || 'normal',
    special_requirements: requestData?.special_requirements || '',
    accessibility_needs: requestData?.accessibility_needs || '',
    existing_contractor: requestData?.existing_contractor || false,
    contractor_details: requestData?.contractor_details || '',
    contact_name: requestData?.contact_name || '',
    contact_email: requestData?.contact_email || '',
    contact_phone: requestData?.contact_phone || '',
    company_name: requestData?.company_name || '',
    lead_id: requestData?.lead_id || initialLeadId || '',
    customer_id: requestData?.customer_id || initialCustomerId || '',
    property_id: requestData?.property_id || requestData?.lead_project_id || '',
    lead_project_id: requestData?.lead_project_id || '',
    internal_notes: requestData?.internal_notes || '',
    review_notes: requestData?.review_notes || '',
  })
  const [requestNotes, setRequestNotes] = useState<ProposalNote[]>(
    parseRequestNotes(requestData?.notes)
  )
  const [attachments, setAttachments] = useState<Attachment[]>(requestData?.attachments || [])
  const [blueprintLinks, setBlueprintLinks] = useState<BlueprintLink[]>(requestData?.blueprint_links || [])
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [linkedProposalId, setLinkedProposalId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<ProposalRequestTab>('overview')
  const [activities, setActivities] = useState<ActivityLog[]>([])
  const [activitiesLoading, setActivitiesLoading] = useState(false)
  const [newNote, setNewNote] = useState('')
  const [savingNote, setSavingNote] = useState(false)

  // Modal state for creating new property/project
  const [showCreatePropertyModal, setShowCreatePropertyModal] = useState(false)
  const [creatingProperty, setCreatingProperty] = useState(false)
  const [newPropertyData, setNewPropertyData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    property_type: '',
    building_type: '',
    unit_count: '',
  })
  const navigate = useNavigate()

  React.useEffect(() => {
    fetchLeadsAndCustomers()
    fetchServiceCategories()
  }, [])

  React.useEffect(() => {
    const requestId = editingRequest?.id || viewingRequest?.id
    if (!requestId) return

    const loadActivities = async () => {
      setActivitiesLoading(true)
      try {
        const acts = await getEntityActivities('proposal', requestId)
        setActivities(acts)
      } finally {
        setActivitiesLoading(false)
      }
    }
    loadActivities()
  }, [editingRequest?.id, viewingRequest?.id])

  // Fetch linked proposal if request is converted
  React.useEffect(() => {
    const fetchLinkedProposal = async () => {
      if (requestData?.id && requestData?.status === 'converted') {
        try {
          const { data, error } = await supabase
            .from('proposals')
            .select('id')
            .eq('id', requestData.id)
            .eq('record_type', 'proposal')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle()

          if (!error && data) {
            setLinkedProposalId(data.id)
          } else if (error && error.code !== 'PGRST116') {
            // PGRST116 is "not found" which is fine, but log other errors
            console.error('Error fetching linked proposal:', error)
          }
        } catch (error) {
          console.error('Error fetching linked proposal:', error)
        }
      }
    }

    fetchLinkedProposal()
  }, [requestData?.id, requestData?.status])

  // Load projects/properties when editing/viewing and requestData is available
  React.useEffect(() => {
    if (requestData) {
      console.log('Loading projects/properties for requestData:', {
        requestData_id: requestData.id,
        lead_id: requestData.lead_id,
        customer_id: requestData.customer_id,
        property_id: requestData.property_id,
        property_address: requestData.property_address,
        ownerType,
      })
      if (requestData.lead_id && ownerType === 'lead') {
        console.log('Fetching projects for lead:', requestData.lead_id)
        fetchProjectsForLead(requestData.lead_id)
      } else if (requestData.customer_id && ownerType === 'customer') {
        console.log('Fetching properties for customer:', requestData.customer_id)
        fetchPropertiesForCustomer(requestData.customer_id)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestData?.id, requestData?.lead_id, requestData?.customer_id, ownerType])

  // Load projects/properties when owner type and IDs are set (for create mode or when formData changes)
  React.useEffect(() => {
    console.log('FormData change effect:', {
      ownerType,
      lead_id: formData.lead_id,
      customer_id: formData.customer_id,
      property_id: formData.property_id,
    })
    if (ownerType === 'lead' && formData.lead_id) {
      console.log('Fetching projects for lead (formData):', formData.lead_id)
      fetchProjectsForLead(formData.lead_id)
    } else if (ownerType === 'customer' && formData.customer_id) {
      console.log('Fetching properties for customer (formData):', formData.customer_id)
      fetchPropertiesForCustomer(formData.customer_id)
    }
  }, [ownerType, formData.lead_id, formData.customer_id])

  // Map legacy category-based project_type to service_type when categories load
  React.useEffect(() => {
    if (!formData.service_type && formData.project_type && serviceCategories.length > 0) {
      const mapped = mapRequestProjectTypeToServiceType(
        formData.project_type,
        serviceCategories
      )
      if (mapped) {
        setFormData((prev) => ({ ...prev, service_type: mapped }))
      }
    }
  }, [serviceCategories, formData.service_type, formData.project_type])

  // Debug: Track projects state changes and auto-select if only one project
  React.useEffect(() => {
    console.log('Projects state changed:', {
      projectsLength: projects.length,
      projects: projects,
      formData_property_id: formData.property_id,
      requestData_property_id: requestData?.property_id,
    })

    // Auto-select project if editing/viewing and there's exactly one project and no property_id is set
    if (requestData && projects.length === 1 && !formData.property_id) {
      const project = projects[0]
      const projectId = project.id || 'project-0'
      console.log('Auto-selecting single project in useEffect:', { project, projectId })
      setFormData((prev) => ({
        ...prev,
        property_id: projectId,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects])

  // Auto-populate property_type when property is selected (for customers only)
  // Projects don't have property_type - only properties do
  React.useEffect(() => {
    if (ownerType === 'customer' && formData.property_id && properties.length > 0) {
      const selectedProperty = properties.find((p) => p.id === formData.property_id)
      console.log('useEffect - Auto-populate property_type:', {
        ownerType,
        property_id: formData.property_id,
        propertiesLength: properties.length,
        selectedProperty,
        propertyType: selectedProperty?.property_type,
        currentPropertyType: formData.property_type,
      })

      if (selectedProperty && selectedProperty.property_type) {
        // Normalize property_type to match dropdown values
        const normalizedPropertyType = normalizePropertyType(selectedProperty.property_type)

        if (normalizedPropertyType && formData.property_type !== normalizedPropertyType) {
          console.log('useEffect: Updating property_type from:', formData.property_type, 'to:', normalizedPropertyType)
          setFormData((prev) => ({
            ...prev,
            property_type: normalizedPropertyType,
          }))
        }
      }
    }
  }, [formData.property_id, ownerType, properties])

  // Auto-populate service_type from lead project work_type when project is selected
  React.useEffect(() => {
    if (isEditMode || isViewMode) {
      return
    }

    if (
      ownerType === 'lead' &&
      formData.property_id &&
      projects.length > 0 &&
      !formData.service_type
    ) {
      const selectedProject = projects.find((p) => p.id === formData.property_id)
      if (selectedProject?.work_type) {
        const mapped = mapRequestProjectTypeToServiceType(
          selectedProject.work_type,
          serviceCategories
        )
        if (mapped) {
          setFormData((prev) => ({
            ...prev,
            service_type: mapped,
          }))
        }
      }
    }
  }, [
    formData.property_id,
    ownerType,
    projects,
    formData.service_type,
    serviceCategories,
    isEditMode,
    isViewMode,
  ])

  // Auto-generate project title from address (used as property name when converted)
  React.useEffect(() => {
    if (isEditMode || isViewMode) {
      return
    }

    if (!formData.property_address) return

    const address = formData.property_address.split(',')[0].trim()
    if (address) {
      setFormData((prev) => ({
        ...prev,
        title: address,
      }))
    }
  }, [formData.property_address, isEditMode, isViewMode])

  const fetchLeadsAndCustomers = async () => {
    try {
      const [leadsResult, customersResult] = await Promise.all([
        supabase
          .from('leads')
          .select('id, contact_first_name, contact_last_name, company_name, email, phone, contacts')
          .order('company_name'),
        supabase
          .from('customers')
          .select('id, company_name, contact_first_name, contact_last_name, email, phone')
          .order('company_name'),
      ])
      setLeads(leadsResult.data || [])
      setCustomers(customersResult.data || [])
    } catch (error) {
      console.error('Error fetching leads and customers:', error)
    }
  }

  const fetchServiceCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('service_categories')
        .select('id, name')
        .eq('is_active', true)
        .order('name')

      if (error) throw error
      setServiceCategories(data || [])
    } catch (error) {
      console.error('Error fetching service categories:', error)
    }
  }

  const fetchProjectsForLead = async (leadId: string) => {
    try {
      console.log('fetchProjectsForLead called with leadId:', leadId)
      const { data: leadData, error } = await supabase
        .from('leads')
        .select('projects, company_name, email, phone, contacts, contact_first_name, contact_last_name')
        .eq('id', leadId)
        .single()
      if (error) throw error

      console.log('Fetched lead data:', {
        leadId,
        projectsRaw: leadData?.projects,
        projectsType: typeof leadData?.projects,
        isArray: Array.isArray(leadData?.projects),
        projectsLength: leadData?.projects?.length,
      })

      // Handle projects - ensure it's an array
      let projectsArray: any[] = []
      if (leadData?.projects) {
        if (Array.isArray(leadData.projects)) {
          projectsArray = [...leadData.projects] // Create a new array to ensure it's properly set
        } else if (typeof leadData.projects === 'string') {
          // If it's a JSON string, parse it
          try {
            const parsed = JSON.parse(leadData.projects)
            projectsArray = Array.isArray(parsed) ? parsed : [parsed]
          } catch (e) {
            console.error('Error parsing projects JSON:', e)
            projectsArray = []
          }
        } else {
          // If it's an object, try to convert it
          projectsArray = [leadData.projects]
        }
      }

      console.log('Processed projects array:', {
        length: projectsArray.length,
        projects: projectsArray,
        firstProject: projectsArray[0],
      })

      setProjects(projectsArray)
      console.log('Projects state set with', projectsArray.length, 'projects')

      // If editing/viewing and property_id is set, try to match it to a project
      if (requestData && projectsArray.length > 0) {
        console.log('Attempting to match property_id:', {
          requestData_property_id: requestData.property_id,
          formData_property_id: formData.property_id,
          projectsArray: projectsArray.map((p: any, i: number) => ({ index: i, id: p.id, address: p.address })),
        })

        let matchedProject: any = null
        let matchedId: string = ''

        // Try to find the project that matches the property_id
        if (requestData.property_id) {
          matchedProject = projectsArray.find((p: any) => {
            if (p.id && p.id.toString() === requestData.property_id.toString()) return true
            return false
          })

          if (!matchedProject && requestData.lead_project_id) {
            matchedProject = projectsArray.find(
              (p: any) => p.id && p.id.toString() === requestData.lead_project_id.toString()
            )
          }

          // If no exact match, try index-based matching
          if (!matchedProject && requestData.property_id.toString().startsWith('project-')) {
            const index = parseInt(requestData.property_id.toString().replace('project-', ''))
            if (!isNaN(index) && projectsArray[index]) {
              matchedProject = projectsArray[index]
            }
          }

          // If still no match, try matching by address (for proposal requests, property_id might be the address)
          if (!matchedProject && requestData.property_address) {
            matchedProject = projectsArray.find((p: any) => {
              return p.address && p.address === requestData.property_address
            })
          }
        }

        // If no property_id but we have exactly one project, auto-select it
        if (!matchedProject && projectsArray.length === 1) {
          matchedProject = projectsArray[0]
          console.log('Auto-selecting single project:', matchedProject)
        }

        if (matchedProject) {
          matchedId = matchedProject.id || `project-${projectsArray.indexOf(matchedProject)}`
          console.log('Matched project:', {
            matchedProject,
            matchedId,
            currentPropertyId: formData.property_id,
            requestPropertyId: requestData.property_id,
          })

          // Update formData to ensure property_id matches
          setFormData((prev) => {
            if (prev.property_id !== matchedId) {
              console.log('Updating property_id from', prev.property_id, 'to', matchedId)
              return {
                ...prev,
                property_id: matchedId,
                lead_project_id: matchedId,
              }
            }
            return prev
          })
        } else {
          console.warn('No project matched for property_id:', requestData.property_id)
        }
      }
    } catch (error) {
      console.error('Error fetching projects:', error)
      setProjects([])
    }
  }

  const fetchPropertiesForCustomer = async (customerId: string) => {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('id, name, address, customer_id, property_type, city, state, zip_code, site_contact_name, site_contact_email, site_contact_phone')
        .eq('customer_id', customerId)
        .order('name')
      if (error) throw error
      console.log('Fetched properties:', data)
      setProperties(data || [])
    } catch (error) {
      console.error('Error fetching properties:', error)
      setProperties([])
    }
  }

  // Handler to open create property/project modal
  const handleOpenCreateModal = () => {
    setNewPropertyData({
      name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      property_type: '',
      building_type: '',
      unit_count: '',
    })
    setShowCreatePropertyModal(true)
  }

  // Handler to create new property (for customers)
  const handleCreateProperty = async () => {
    if (!formData.customer_id) {
      setErrors({ ...errors, customer_id: 'Please select a customer first' })
      return
    }

    if (!newPropertyData.name && !newPropertyData.address) {
      setErrors({ ...errors, property: 'Please enter at least a name or address' })
      return
    }

    setCreatingProperty(true)
    try {
      const insertData: any = {
        customer_id: formData.customer_id,
        name: newPropertyData.name || newPropertyData.address || 'New Property',
        address: newPropertyData.address || null,
        city: newPropertyData.city || null,
        state: newPropertyData.state || null,
        zip_code: newPropertyData.zip || null,
        property_type: newPropertyData.property_type || null,
      }

      const { data: property, error } = await supabase
        .from('properties')
        .insert([insertData])
        .select()
        .single()

      if (error) throw error

      // Refresh properties list
      await fetchPropertiesForCustomer(formData.customer_id)

      // Auto-select the newly created property
      setFormData((prev) => ({
        ...prev,
        property_id: property.id,
        property_address: property.address || '',
        property_type: property.property_type || '',
        title:
          property.name?.trim() ||
          property.address?.split(',')[0].trim() ||
          prev.title,
      }))

      // Close modal and reset form
      setShowCreatePropertyModal(false)
      setNewPropertyData({
        name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        property_type: '',
        building_type: '',
        unit_count: '',
      })
      setErrors({})
    } catch (error: any) {
      console.error('Error creating property:', error)
      setErrors({ ...errors, property: 'Error creating property: ' + (error.message || 'Unknown error') })
    } finally {
      setCreatingProperty(false)
    }
  }

  // Handler to create new project (for leads)
  const handleCreateProject = async () => {
    if (!formData.lead_id) {
      setErrors({ ...errors, lead_id: 'Please select a lead first' })
      return
    }

    if (!newPropertyData.address) {
      setErrors({ ...errors, project: 'Please enter an address for the project' })
      return
    }

    if (!newPropertyData.building_type) {
      setErrors({ ...errors, project: 'Please select a building type for the project' })
      return
    }

    setCreatingProperty(true)
    try {
      // Fetch current lead data
      const { data: currentLead, error: fetchError } = await supabase
        .from('leads')
        .select('projects')
        .eq('id', formData.lead_id)
        .single()

      if (fetchError) throw fetchError

      // Create new project object
      const projectTypeFromModal = newPropertyData.property_type || ''
      const newProject = {
        id: `project-${Date.now()}`,
        address: newPropertyData.address,
        city: newPropertyData.city || '',
        state: newPropertyData.state || '',
        zip: newPropertyData.zip || '',
        type: newPropertyData.building_type || '',
        unit_count: newPropertyData.unit_count || '',
        work_type: mapProjectTypeToWorkTypeId(projectTypeFromModal, serviceCategories),
      }

      // Add new project to projects array
      const updatedProjects = [...(currentLead.projects || []), newProject]

      // Update lead with new projects array
      const { error: updateError } = await supabase
        .from('leads')
        .update({ projects: updatedProjects })
        .eq('id', formData.lead_id)

      if (updateError) throw updateError

      // Refresh projects list
      await fetchProjectsForLead(formData.lead_id)

      // Auto-select the newly created project
      const mappedFromModal = projectTypeFromModal
        ? mapRequestProjectTypeToServiceType(
            mapProjectTypeToWorkTypeId(projectTypeFromModal, serviceCategories),
            serviceCategories
          ) || projectTypeFromModal
        : ''

      setFormData((prev) => ({
        ...prev,
        property_id: newProject.id,
        lead_project_id: newProject.id,
        property_address: newProject.address,
        property_city: newProject.city,
        property_state: newProject.state,
        property_zip: newProject.zip,
        title: newProject.address?.split(',')[0].trim() || prev.title,
        service_type: prev.service_type || mappedFromModal,
        property_type:
          prev.property_type ||
          (newPropertyData.building_type
            ? normalizePropertyType(newPropertyData.building_type)
            : ''),
      }))

      // Close modal and reset form
      setShowCreatePropertyModal(false)
      setNewPropertyData({
        name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        property_type: '',
        building_type: '',
        unit_count: '',
      })
      setErrors({})
    } catch (error: any) {
      console.error('Error creating project:', error)
      setErrors({ ...errors, project: 'Error creating project: ' + (error.message || 'Unknown error') })
    } finally {
      setCreatingProperty(false)
    }
  }

  const propertyTypes = [
    { value: 'residential', label: 'Residential' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'multi_unit', label: 'Multi-Unit Building' },
    { value: 'office', label: 'Office Building' },
    { value: 'retail', label: 'Retail Space' },
    { value: 'warehouse', label: 'Warehouse' },
    { value: 'other', label: 'Other' },
  ]

  // Helper function to normalize and map property types to dropdown values
  const normalizePropertyType = (propertyType: string): string => {
    if (!propertyType) return ''

    // Normalize: lowercase, replace spaces with underscores
    const normalized = propertyType.toLowerCase().replace(/\s+/g, '_').trim()

    // Map common variations to dropdown values
    const typeMapping: { [key: string]: string } = {
      'office_building': 'office',
      'residential_building': 'residential',
      'commercial_building': 'commercial',
      'multi_unit_building': 'multi_unit',
      'multi-unit': 'multi_unit',
      'office': 'office',
      'residential': 'residential',
      'commercial': 'commercial',
      'retail': 'retail',
      'warehouse': 'warehouse',
    }

    // Check if normalized value exists in mapping
    if (typeMapping[normalized]) {
      return typeMapping[normalized]
    }

    // Check if normalized value exists in propertyTypes options
    const existsInOptions = propertyTypes.some((pt) => pt.value === normalized)
    if (existsInOptions) {
      return normalized
    }

    // If no match found, return normalized value anyway (user can manually change)
    return normalized
  }

  const urgencyLevels = [
    { value: 'low', label: 'Low Priority' },
    { value: 'normal', label: 'Normal Priority' },
    { value: 'high', label: 'High Priority' },
    { value: 'urgent', label: 'Urgent' },
  ]

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.lead_id && !formData.customer_id) {
      newErrors.owner = 'Please select a lead or customer'
    }
    if (!formData.title.trim()) newErrors.title = 'Project title is required'
    if (!hasLinkedSite && !formData.property_address.trim()) {
      newErrors.property_address = 'Property address is required'
    }
    if (!effectiveContactName.trim()) newErrors.contact_name = 'Contact name is required'
    if (!effectiveContactEmail.trim()) newErrors.contact_email = 'Contact email is required'
    if (!formData.service_type) newErrors.service_type = 'Service type is required'
    if (!formData.property_type) newErrors.property_type = 'Property type is required'

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (effectiveContactEmail && !emailRegex.test(effectiveContactEmail)) {
      newErrors.contact_email = 'Please enter a valid email address'
    }

    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) {
      setActiveTab(tabForFirstError(newErrors))
    }
    return Object.keys(newErrors).length === 0
  }

  const validateTab = (tab: ProposalRequestTab): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    switch (tab) {
      case 'overview':
        return !!(
          (formData.lead_id || formData.customer_id) &&
          effectiveContactName.trim() &&
          effectiveContactEmail.trim() &&
          emailRegex.test(effectiveContactEmail)
        )
      case 'details':
        return !!(
          formData.title.trim() &&
          (hasLinkedSite || formData.property_address.trim()) &&
          formData.service_type &&
          formData.property_type
        )
      case 'documents':
      case 'notes':
        return true
      default:
        return false
    }
  }

  const validateTabWithErrors = (tab: ProposalRequestTab): boolean => {
    const newErrors: Record<string, string> = {}
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (tab === 'overview') {
      if (!formData.lead_id && !formData.customer_id) {
        newErrors.owner = 'Please select a lead or customer'
      }
      if (!effectiveContactName.trim()) newErrors.contact_name = 'Contact name is required'
      if (!effectiveContactEmail.trim()) newErrors.contact_email = 'Contact email is required'
      else if (!emailRegex.test(effectiveContactEmail)) {
        newErrors.contact_email = 'Please enter a valid email address'
      }
    }

    if (tab === 'details') {
      if (!formData.title.trim()) newErrors.title = 'Project title is required'
      if (!hasLinkedSite && !formData.property_address.trim()) {
        newErrors.property_address = 'Property address is required'
      }
      if (!formData.service_type) newErrors.service_type = 'Service type is required'
      if (!formData.property_type) newErrors.property_type = 'Property type is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    if (!validateTabWithErrors(activeTab)) return

    const currentIndex = TAB_ORDER.indexOf(activeTab)
    if (currentIndex < TAB_ORDER.length - 1) {
      setActiveTab(TAB_ORDER[currentIndex + 1])
    }
  }

  const handleBack = () => {
    const currentIndex = TAB_ORDER.indexOf(activeTab)
    if (currentIndex > 0) {
      setActiveTab(TAB_ORDER[currentIndex - 1])
    }
  }

  const handleAddNote = async () => {
    if (!newNote.trim() || isViewMode) return

    const noteToAdd: ProposalNote = {
      note: newNote.trim(),
      timestamp: new Date().toISOString(),
    }

    if (isEditMode && editingRequest?.id) {
      setSavingNote(true)
      try {
        const updatedNotes = [noteToAdd, ...requestNotes]
        const { error } = await supabase
          .from('proposals')
          .update({ notes: updatedNotes })
          .eq('id', editingRequest.id)
          .eq('record_type', 'request')

        if (error) throw error
        setRequestNotes(updatedNotes)
        setNewNote('')
        await logActivity({
          activity_type: 'note_added',
          entity_type: 'proposal',
          entity_id: editingRequest.id,
          description: 'Note added to proposal request',
        })
        const acts = await getEntityActivities('proposal', editingRequest.id)
        setActivities(acts)
      } catch (error) {
        console.error('Error adding note:', error)
        alert('Error adding note')
      } finally {
        setSavingNote(false)
      }
    } else {
      setRequestNotes([noteToAdd, ...requestNotes])
      setNewNote('')
    }
  }

  const panelNotes: PanelNote[] = requestNotes.map((n) => ({
    note: n.note,
    timestamp: n.timestamp,
  }))

  const handleFileUpload = async (file: File) => {
    try {
      // In a real implementation, you would upload to a file storage service
      // For now, we'll simulate the upload
      const attachment: Attachment = {
        name: file.name,
        url: URL.createObjectURL(file), // Temporary URL for demo
        type: file.type,
        size: file.size,
        uploaded_at: new Date().toISOString(),
      }
      setAttachments((prev) => [...prev, attachment])
    } catch (error) {
      console.error('File upload error:', error)
      alert('Error uploading file')
    }
  }

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  const addBlueprintLink = () => {
    setBlueprintLinks((prev) => [...prev, { title: '', url: '', description: '' }])
  }

  const updateBlueprintLink = (index: number, field: keyof BlueprintLink, value: string) => {
    setBlueprintLinks((prev) =>
      prev.map((link, i) => (i === index ? { ...link, [field]: value } : link))
    )
  }

  const removeBlueprintLink = (index: number) => {
    setBlueprintLinks((prev) => prev.filter((_, i) => i !== index))
  }

  const handleAddressSelect = (address: {
    address: string
    city: string
    state: string
    zip: string
  }) => {
    setFormData((prev) => ({
      ...prev,
      property_address: address.address,
      property_city: address.city,
      property_state: address.state,
      property_zip: address.zip,
    }))
  }

  // Helper function to validate UUID format
  const isValidUUID = (value: string): boolean => {
    if (!value || value.trim() === '') return false
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    return uuidRegex.test(value)
  }

  // Helper function to sanitize UUID values (convert invalid ones to null)
  const sanitizeUUID = (value: string): string | null => {
    if (!value || value.trim() === '') return null
    return isValidUUID(value) ? value : null
  }

  const inputClassName = (baseClassName: string, hasError = false) => {
    return `${baseClassName} ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''} ${hasError ? 'border-red-500' : ''}`
  }

  const hasLinkedSite = Boolean(
    formData.property_id &&
      ((ownerType === 'lead' && formData.lead_id) ||
        (ownerType === 'customer' && formData.customer_id))
  )

  const ownerLinked = hasLinkedOwner(
    ownerType === 'customer' ? formData.customer_id : '',
    ownerType === 'lead' ? formData.lead_id : ''
  )

  const resolvedOwnerContact = React.useMemo(() => {
    const customer =
      ownerType === 'customer'
        ? customers.find((c) => c.id === formData.customer_id)
        : null
    const lead =
      ownerType === 'lead' ? leads.find((l) => l.id === formData.lead_id) : null
    const property =
      ownerType === 'customer' && formData.property_id
        ? properties.find((p) => p.id === formData.property_id)
        : null
    const leadProject =
      ownerType === 'lead' && projects.length > 0
        ? projects.find(
            (p: ProjectItem, index: number) =>
              (p.id && String(p.id) === String(formData.lead_project_id || formData.property_id)) ||
              `project-${index}` === formData.property_id
          )
        : null

    return resolveOwnerContact({
      customer,
      lead,
      property,
      leadProject,
    })
  }, [
    ownerType,
    formData.customer_id,
    formData.lead_id,
    formData.property_id,
    formData.lead_project_id,
    customers,
    leads,
    properties,
    projects,
  ])

  const effectiveContactName = ownerLinked
    ? resolvedOwnerContact.contact_name
    : formData.contact_name
  const effectiveContactEmail = ownerLinked
    ? resolvedOwnerContact.contact_email
    : formData.contact_email
  const effectiveContactPhone = ownerLinked
    ? resolvedOwnerContact.contact_phone
    : formData.contact_phone
  const effectiveCompanyName = ownerLinked
    ? resolvedOwnerContact.company_name
    : formData.company_name

  const linkedSiteLabel =
    formData.title?.trim() ||
    formData.property_address?.trim() ||
    'Linked site'

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()

    if (isViewMode) {
      // In view mode, just close
      onClose()
      return
    }

    if (!validateForm()) return

    setLoading(true)
    try {
      const { supabase } = await import('../lib/supabase')

      console.log('Submitting proposal request with data:', {
        lead_id: formData.lead_id,
        customer_id: formData.customer_id,
        property_id: formData.property_id,
        service_type: formData.service_type,
        sanitized_lead_id: sanitizeUUID(formData.lead_id),
        sanitized_customer_id: sanitizeUUID(formData.customer_id),
        sanitized_property_id: sanitizeUUID(formData.property_id),
      })

      const serviceType = formData.service_type?.trim() || ''

      const addressFields = hasLinkedSite
        ? {
            property_address: null,
            property_city: null,
            property_state: null,
            property_zip: null,
          }
        : {
            property_address: formData.property_address,
            property_city: formData.property_city,
            property_state: formData.property_state,
            property_zip: formData.property_zip,
          }

      const contactFields = ownerLinked
        ? {
            contact_name: null,
            contact_email: null,
            contact_phone: null,
            company_name: null,
          }
        : {
            contact_name: formData.contact_name,
            contact_email: formData.contact_email,
            contact_phone: formData.contact_phone,
            company_name: formData.company_name,
          }

      const requestData = {
        title: formData.title,
        description: formData.description,
        project_type: serviceType || null,
        service_type: serviceType || null,
        ...addressFields,
        property_type: formData.property_type,
        square_footage: formData.square_footage ? parseInt(formData.square_footage) : null,
        estimated_budget: formData.estimated_budget ? parseFloat(formData.estimated_budget) : null,
        preferred_start_date: formData.preferred_start_date || null,
        preferred_completion_date: formData.preferred_completion_date || null,
        urgency_level: formData.urgency_level,
        special_requirements: formData.special_requirements,
        accessibility_needs: formData.accessibility_needs,
        existing_contractor: formData.existing_contractor,
        contractor_details: formData.contractor_details,
        ...contactFields,
        lead_id: sanitizeUUID(formData.lead_id),
        customer_id: sanitizeUUID(formData.customer_id),
        property_id: sanitizeUUID(formData.property_id),
        lead_project_id:
          ownerType === 'lead' && formData.lead_project_id
            ? formData.lead_project_id
            : null,
        internal_notes: formData.internal_notes || null,
        review_notes: formData.review_notes || null,
        notes: requestNotes.length > 0 ? requestNotes : null,
        attachments: attachments,
        blueprint_links: blueprintLinks,
      }

      let savedId: string | null = null
      let savedRequestNumber: string | null = null
      let error
      if (isEditMode && editingRequest?.id) {
        const { error: updateError } = await supabase
          .from('proposals')
          .update({ ...requestData, record_type: 'request' })
          .eq('id', editingRequest.id)
          .eq('record_type', 'request')
        error = updateError
        savedId = editingRequest.id
        savedRequestNumber = editingRequest.request_number || null
      } else {
        const { data: inserted, error: insertError } = await supabase
          .from('proposals')
          .insert({
            ...requestData,
            record_type: 'request',
            status: 'submitted',
            submitted_at: new Date().toISOString(),
          })
          .select('id, request_number')
          .single()
        error = insertError
        if (inserted) {
          savedId = inserted.id
          savedRequestNumber = inserted.request_number || null
        }
      }

      if (error) throw error

      if (savedId) {
        const label = savedRequestNumber || savedId
        const parentDescription = isEditMode
          ? `Proposal request ${label} updated — ${formData.title || 'Untitled'}`
          : `Proposal request ${label} submitted — ${formData.title || 'Untitled'}`

        const activityType = isEditMode
          ? ActivityTypes.PROPOSAL_REQUEST_UPDATED
          : ActivityTypes.PROPOSAL_REQUEST_CREATED

        const activityMeta = {
          child_entity_type: 'proposal_request',
          child_entity_id: savedId,
          request_number: savedRequestNumber,
          title: formData.title,
        }

        await logActivity({
          activity_type: activityType,
          entity_type: 'proposal',
          entity_id: savedId,
          description: parentDescription,
          metadata: activityMeta,
        })

        await logActivityOnLinkedParents({
          parents: {
            lead_id: sanitizeUUID(formData.lead_id),
            customer_id: sanitizeUUID(formData.customer_id),
            property_id: sanitizeUUID(formData.property_id),
          },
          activity_type: activityType,
          description: parentDescription,
          metadata: activityMeta,
        })
      }

      setNotification({
        message: isEditMode ? 'Proposal request updated successfully!' : 'Proposal request submitted successfully!',
        type: 'success',
      })

      // Auto-close after showing success message
      setTimeout(() => {
        onSuccess()
        onClose()
      }, 1500)
    } catch (error) {
      console.error('Error submitting proposal request:', error)
      setNotification({
        message: isEditMode ? 'Error updating proposal request' : 'Error submitting proposal request',
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const formContent = (
    <div
      className={
        isFullPage
          ? mobile.surface
          : 'bg-white rounded-t-2xl sm:rounded-2xl max-w-4xl w-full max-h-[90dvh] overflow-y-auto shadow-xl'
      }
    >
      <div className="p-4 sm:p-6">
          {/* Notification */}
          {notification && (
            <div className="mb-4">
              <div
                className={`p-4 rounded-xl border ${
                  notification.type === 'success'
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <p
                    className={`text-sm font-medium ${
                      notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                    }`}
                  >
                    {notification.message}
                  </p>
                  <button
                    type="button"
                    onClick={() => setNotification(null)}
                    className={mobile.ghostBtn}
                  >
                    <XIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {!isFullPage && (
            <div className="flex items-center justify-between mb-6">
              <div className="flex-1">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {isViewMode
                    ? 'View Proposal Request'
                    : isEditMode
                      ? 'Edit Proposal Request'
                      : 'Submit Proposal Request'}
                </h2>
                {isViewMode && requestData?.status === 'converted' && linkedProposalId && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        onClose()
                        navigate(`/proposals/view/${linkedProposalId}`)
                      }}
                      className="inline-flex items-center gap-2 min-h-[44px] px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-colors"
                    >
                      <ExternalLinkIcon className="w-4 h-4" />
                      View Linked Proposal
                    </button>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className={mobile.ghostBtn}
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
          )}

          {isFullPage && isViewMode && requestData?.status === 'converted' && linkedProposalId && (
            <div className="mb-4">
              <button
                type="button"
                onClick={() => {
                  onClose()
                  navigate(`/proposals/view/${linkedProposalId}`)
                }}
                className="inline-flex items-center gap-2 min-h-[44px] px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-colors"
              >
                <ExternalLinkIcon className="w-4 h-4" />
                View Linked Proposal
              </button>
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault()
            }}
            className="space-y-4 sm:space-y-6"
          >
            <div className={`${isFullPage ? 'sticky top-[3.25rem] z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 bg-white/95 backdrop-blur-md' : ''} flex overflow-x-auto scrollbar-none border-b border-gray-200`}>
              {(
                [
                  { id: 'overview' as const, label: 'Overview', short: 'Overview' },
                  { id: 'details' as const, label: 'Details', short: 'Details' },
                  { id: 'documents' as const, label: 'Documents', short: 'Docs' },
                  { id: 'notes' as const, label: 'Notes & Activity', short: 'Notes' },
                ] as const
              ).map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 sm:px-5 min-h-[48px] flex items-center text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <span className="sm:hidden">{tab.short}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                  {tab.id === 'documents' &&
                    attachments.length + blueprintLinks.length > 0 && (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                        {attachments.length + blueprintLinks.length}
                      </span>
                    )}
                </button>
              ))}
            </div>

            {activeTab === 'overview' && (
              <div className="space-y-4">
            <RequestSectionPanel
              title="Lead or Customer"
              icon={<UserIcon className="h-4 w-4" />}
              variant="neutral"
            >
              <LeadCustomerSearchSelect
                leads={leads}
                customers={customers}
                value={selectionFromIds(formData.lead_id, formData.customer_id)}
                onChange={(selection) => {
                  if (!selection) {
                    setOwnerType('lead')
                    setFormData((prev) => ({
                      ...prev,
                      lead_id: '',
                      customer_id: '',
                      property_id: '',
                      lead_project_id: '',
                    }))
                    return
                  }

                  if (selection.type === 'lead') {
                    setOwnerType('lead')
                    setFormData((prev) => ({
                      ...prev,
                      lead_id: selection.id,
                      customer_id: '',
                      property_id: '',
                      lead_project_id: '',
                    }))
                  } else {
                    setOwnerType('customer')
                    setFormData((prev) => ({
                      ...prev,
                      customer_id: selection.id,
                      lead_id: '',
                      property_id: '',
                      lead_project_id: '',
                    }))
                  }
                }}
                disabled={isViewMode || isEditMode}
                label="Lead or Customer"
                placeholder="Type to search leads and customers..."
              />
              {errors.owner && <p className="text-red-500 text-sm mt-1">{errors.owner}</p>}
            </RequestSectionPanel>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {ownerType === 'lead' && formData.lead_id && (
              <RequestSectionPanel title="Select Project" variant="entity">
                <PropertyProjectSearchSelect
                  mode="project"
                  projects={projects as ProjectItem[]}
                  value={formData.property_id}
                  onChange={(id, option) => {
                    if (!option) {
                      setFormData((prev) => ({
                        ...prev,
                        property_id: '',
                        lead_project_id: '',
                        service_type: '',
                        property_type: '',
                      }))
                      return
                    }

                    const project = option.raw as ProjectItem
                    const projectId = project.id || id
                    const mappedService = project.work_type
                      ? mapRequestProjectTypeToServiceType(project.work_type, serviceCategories)
                      : ''
                    setFormData((prev) => ({
                      ...prev,
                      property_id: projectId,
                      lead_project_id: projectId,
                      property_address: project.address || '',
                      property_city: project.city || '',
                      property_state: project.state || '',
                      property_zip: project.zip || '',
                      title: project.address?.split(',')[0].trim() || prev.title,
                      service_type: prev.service_type || mappedService || '',
                      property_type:
                        prev.property_type ||
                        (project.type ? normalizePropertyType(project.type) : ''),
                    }))
                  }}
                  onCreate={handleOpenCreateModal}
                  disabled={isViewMode}
                  label="Project"
                  placeholder="Type to search projects..."
                  id="proposal-request-project-search"
                />
                {projects.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">No projects found for this lead yet.</p>
                )}
              </RequestSectionPanel>
            )}

            {ownerType === 'customer' && formData.customer_id && properties.length === 0 && (
              <p className="text-xs text-amber-600 lg:col-span-2">
                No properties found for this customer. Add a property or select another customer.
              </p>
            )}

            {ownerType === 'customer' && formData.customer_id && (
              <RequestSectionPanel title="Select Property" variant="entity">
                <PropertyProjectSearchSelect
                  mode="property"
                  properties={properties as PropertyItem[]}
                  value={formData.property_id}
                  onChange={(_id, option) => {
                    if (!option) {
                      setFormData((prev) => ({
                        ...prev,
                        property_id: '',
                        lead_project_id: '',
                        property_type: '',
                      }))
                      return
                    }

                    const property = option.raw as PropertyItem
                    const updates: Record<string, string> = {
                      property_id: property.id,
                      lead_project_id: '',
                      property_address: property.address || '',
                      title:
                        property.name?.trim() ||
                        property.address?.split(',')[0].trim() ||
                        '',
                    }

                    if (property.property_type) {
                      updates.property_type = normalizePropertyType(property.property_type)
                    }

                    setFormData((prev) => ({ ...prev, ...updates, title: updates.title || prev.title }))
                  }}
                  onCreate={handleOpenCreateModal}
                  disabled={isViewMode}
                  label="Property"
                  placeholder="Type to search properties..."
                  id="proposal-request-property-search"
                />
              </RequestSectionPanel>
            )}
            </div>

            <RequestSectionPanel
              title={ownerLinked ? 'Contact (from owner)' : 'Contact on this request'}
              icon={<UserIcon className="h-4 w-4" />}
              variant="neutral"
            >
              {ownerLinked && !isViewMode && (
                <p className="text-xs text-gray-500 mb-3">
                  Contact is managed on the linked {ownerType}. Update it there to change contact
                  on all related records.
                </p>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Name *
                  </label>
                  <input
                    type="text"
                    value={effectiveContactName}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    readOnly={isViewMode || ownerLinked}
                    className={inputClassName(`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`, !!errors.contact_name)}
                    placeholder="John Doe"
                  />
                  {errors.contact_name && (
                    <p className="text-red-500 text-sm mt-1">{errors.contact_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Email *
                  </label>
                  <input
                    type="email"
                    value={effectiveContactEmail}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    readOnly={isViewMode || ownerLinked}
                    className={inputClassName(`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`, !!errors.contact_email)}
                    placeholder="john@example.com"
                  />
                  {errors.contact_email && (
                    <p className="text-red-500 text-sm mt-1">{errors.contact_email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={effectiveContactPhone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    readOnly={isViewMode || ownerLinked}
                    className={inputClassName("w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500")}
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={effectiveCompanyName}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    readOnly={isViewMode || ownerLinked}
                    className={inputClassName("w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500")}
                    placeholder="ABC Corporation"
                  />
                </div>
              </div>
            </RequestSectionPanel>
              </div>
            )}

            {activeTab === 'details' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="space-y-3">
            <RequestSectionPanel
              title="Project scope"
              icon={<FileTextIcon className="h-4 w-4" />}
              variant="scope"
            >

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    readOnly={isViewMode}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.title ? 'border-red-500' : 'border-gray-300'
                    } ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="e.g., Office Building Deep Clean"
                  />
                  {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Type *
                  </label>
                  <select
                    value={formData.service_type}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, service_type: e.target.value }))
                    }}
                    disabled={isViewMode}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.service_type ? 'border-red-500' : 'border-gray-300'
                    } ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  >
                    <option value="">Select service type...</option>
                    {SERVICE_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {!isViewMode && (
                    <p className="text-xs text-gray-500 mt-1">
                      Same options as proposals — sets job type and template when you create a
                      proposal.
                    </p>
                  )}
                  {errors.service_type && (
                    <p className="text-red-500 text-sm mt-1">{errors.service_type}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    readOnly={isViewMode}
                    rows={3}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="Describe the project requirements, scope, and any specific needs..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Property Type *
                  </label>
                  <select
                    value={formData.property_type}
                    onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                    disabled={isViewMode}
                    className={inputClassName(`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`, !!errors.property_type)}
                  >
                    <option value="">Select property type</option>
                    {propertyTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {errors.property_type && (
                    <p className="text-red-500 text-sm mt-1">{errors.property_type}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Square Footage
                  </label>
                  <input
                    type="number"
                    value={formData.square_footage}
                    onChange={(e) => setFormData({ ...formData, square_footage: e.target.value })}
                    readOnly={isViewMode}
                    className={inputClassName("w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500")}
                    placeholder="2500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Budget
                  </label>
                  <div className="relative">
                    <DollarSignIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="number"
                      step="0.01"
                      value={formData.estimated_budget}
                      onChange={(e) =>
                        setFormData({ ...formData, estimated_budget: e.target.value })
                      }
                      readOnly={isViewMode}
                      className={inputClassName("w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500")}
                      placeholder="5000.00"
                    />
                  </div>
                </div>
              </div>
            </RequestSectionPanel>

            {hasLinkedSite ? (
              <RequestSectionPanel
                title={ownerType === 'lead' ? 'Linked project' : 'Linked property'}
                icon={<MapPinIcon className="h-4 w-4" />}
                variant="entity"
              >
                <p className="text-sm text-gray-900">{linkedSiteLabel}</p>
                {!isViewMode && (
                  <p className="text-xs text-gray-500 mt-1">
                    To change the linked {ownerType === 'lead' ? 'project' : 'property'}, go to the
                    Overview tab.
                  </p>
                )}
              </RequestSectionPanel>
            ) : (
              <RequestSectionPanel
                title="Property address"
                icon={<MapPinIcon className="h-4 w-4" />}
                variant="scope"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Property Address *
                    </label>
                    <AddressAutocomplete
                      value={formData.property_address}
                      onChange={(value) => setFormData({ ...formData, property_address: value })}
                      onAddressSelect={handleAddressSelect}
                      placeholder="Start typing address..."
                      disabled={isViewMode}
                      className={errors.property_address ? 'border-red-500' : ''}
                    />
                    {errors.property_address && (
                      <p className="text-red-500 text-sm mt-1">{errors.property_address}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                    <input
                      type="text"
                      value={formData.property_city}
                      onChange={(e) => setFormData({ ...formData, property_city: e.target.value })}
                      readOnly={isViewMode}
                      className={inputClassName("w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500")}
                      placeholder="New York"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                    <input
                      type="text"
                      value={formData.property_state}
                      onChange={(e) => setFormData({ ...formData, property_state: e.target.value })}
                      readOnly={isViewMode}
                      className={inputClassName("w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500")}
                      placeholder="NY"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
                    <input
                      type="text"
                      value={formData.property_zip}
                      onChange={(e) => setFormData({ ...formData, property_zip: e.target.value })}
                      readOnly={isViewMode}
                      className={inputClassName("w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500")}
                      placeholder="10001"
                    />
                  </div>
                </div>
              </RequestSectionPanel>
            )}
            </div>

            <div className="space-y-3">
            <RequestSectionPanel
              title="Timeline"
              icon={<CalendarIcon className="h-4 w-4" />}
              variant="scope"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.preferred_start_date}
                    onChange={(e) =>
                      setFormData({ ...formData, preferred_start_date: e.target.value })
                    }
                    readOnly={isViewMode}
                    className={inputClassName("w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500")}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Completion Date
                  </label>
                  <input
                    type="date"
                    value={formData.preferred_completion_date}
                    onChange={(e) =>
                      setFormData({ ...formData, preferred_completion_date: e.target.value })
                    }
                    readOnly={isViewMode}
                    className={inputClassName("w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500")}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Urgency Level
                  </label>
                  <select
                    value={formData.urgency_level}
                    onChange={(e) => setFormData({ ...formData, urgency_level: e.target.value })}
                    disabled={isViewMode}
                    className={inputClassName("w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500")}
                  >
                    {urgencyLevels.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </RequestSectionPanel>

            <RequestSectionPanel
              title="Special requirements"
              icon={<AlertCircleIcon className="h-4 w-4" />}
              variant="scope"
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Special Requirements
                  </label>
                  <textarea
                    value={formData.special_requirements}
                    onChange={(e) =>
                      setFormData({ ...formData, special_requirements: e.target.value })
                    }
                    readOnly={isViewMode}
                    rows={3}
                    className={inputClassName("w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500")}
                    placeholder="Any special requirements, preferences, or constraints..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Access Info
                  </label>
                  <textarea
                    value={formData.accessibility_needs}
                    onChange={(e) =>
                      setFormData({ ...formData, accessibility_needs: e.target.value })
                    }
                    readOnly={isViewMode}
                    rows={2}
                    className={inputClassName("w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500")}
                    placeholder="Gate codes, entry instructions, parking, who to contact for access..."
                  />
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.existing_contractor}
                      onChange={(e) =>
                        setFormData({ ...formData, existing_contractor: e.target.checked })
                      }
                      disabled={isViewMode}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Working with existing contractor</span>
                  </label>
                </div>

                {formData.existing_contractor && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contractor Details
                    </label>
                    <textarea
                      value={formData.contractor_details}
                      onChange={(e) =>
                        setFormData({ ...formData, contractor_details: e.target.value })
                      }
                      readOnly={isViewMode}
                      rows={2}
                      className={inputClassName("w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500")}
                      placeholder="Details about the existing contractor and their role..."
                    />
                  </div>
                )}
              </div>
            </RequestSectionPanel>
            </div>
              </div>
            )}

            {activeTab === 'documents' && (
              <div className="space-y-6">
            {/* File Attachments */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <UploadIcon className="h-5 w-5" />
                File Attachments
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Files
                  </label>
                  <input
                    type="file"
                    multiple
                    onChange={(e) => {
                      if (e.target.files) {
                        Array.from(e.target.files).forEach(handleFileUpload)
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Upload photos, documents, or other files related to the project
                  </p>
                </div>

                {attachments.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">Uploaded Files:</h4>
                    {attachments.map((attachment, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-white border rounded-lg"
                      >
                        <div className="flex items-center space-x-2">
                          <FileTextIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-700">{attachment.name}</span>
                          <span className="text-xs text-gray-500">
                            ({(attachment.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <XIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Blueprint Links */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <LinkIcon className="h-5 w-5" />
                Blueprint & Document Links
              </h3>

              <div className="space-y-4">
                <button
                  type="button"
                  onClick={addBlueprintLink}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  + Add Blueprint Link
                </button>

                {blueprintLinks.map((link, index) => (
                  <div key={index} className="p-4 bg-white border rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-gray-700">Link {index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => removeBlueprintLink(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <XIcon className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Title
                        </label>
                        <input
                          type="text"
                          value={link.title}
                          onChange={(e) => updateBlueprintLink(index, 'title', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., Floor Plan, Blueprint"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                        <input
                          type="url"
                          value={link.url}
                          onChange={(e) => updateBlueprintLink(index, 'url', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="https://example.com/blueprint.pdf"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={link.description}
                          onChange={(e) =>
                            updateBlueprintLink(index, 'description', e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Brief description of the document"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
              </div>
            )}

            {activeTab === 'notes' && (
              <NotesAndActivityPanel
                notes={panelNotes}
                activities={activities}
                activitiesLoading={activitiesLoading}
                notesTitle="Request Notes"
                newNote={newNote}
                onNewNoteChange={setNewNote}
                onAddNote={handleAddNote}
                savingNote={savingNote}
                readOnly={isViewMode}
                showAddNote={!isViewMode}
                activityEmptyMessage={
                  isEditMode || isViewMode
                    ? 'No recent activity'
                    : 'Activity will appear after the request is saved'
                }
                notesExtra={
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Internal Notes
                      </label>
                      <textarea
                        value={formData.internal_notes}
                        onChange={(e) =>
                          setFormData({ ...formData, internal_notes: e.target.value })
                        }
                        readOnly={isViewMode}
                        rows={3}
                        className={inputClassName(
                          'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                        )}
                        placeholder="Staff-only notes..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Review Notes
                      </label>
                      <textarea
                        value={formData.review_notes}
                        onChange={(e) =>
                          setFormData({ ...formData, review_notes: e.target.value })
                        }
                        readOnly={isViewMode}
                        rows={3}
                        className={inputClassName(
                          'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                        )}
                        placeholder="Review feedback..."
                      />
                    </div>
                  </div>
                }
              />
            )}

            {/* Form navigation */}
            <div
              className={
                isFullPage
                  ? 'native-action-bar flex items-center justify-between gap-3 max-w-6xl mx-auto lg:rounded-xl lg:border lg:border-gray-200'
                  : 'flex items-center justify-between gap-3 pt-6 border-t'
              }
            >
              <button
                type="button"
                onClick={onClose}
                className={mobile.secondaryBtn}
              >
                {isViewMode ? 'Close' : 'Cancel'}
              </button>
              {!isViewMode && (
                <div className="flex gap-2 sm:gap-3">
                  {activeTab !== 'overview' && (
                    <button
                      type="button"
                      onClick={handleBack}
                      className={mobile.secondaryBtn}
                    >
                      Back
                    </button>
                  )}
                  {activeTab === 'notes' ? (
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={loading}
                      className={mobile.primaryBtn}
                    >
                      {loading
                        ? isEditMode
                          ? 'Updating...'
                          : 'Submitting...'
                        : isEditMode
                          ? (
                            <>
                              <span className="sm:hidden">Update</span>
                              <span className="hidden sm:inline">Update Request</span>
                            </>
                          )
                          : (
                            <>
                              <span className="sm:hidden">Submit</span>
                              <span className="hidden sm:inline">Submit Request</span>
                            </>
                          )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleNext}
                      disabled={!validateTab(activeTab)}
                      className={mobile.primaryBtn}
                    >
                      Next
                    </button>
                  )}
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Create Property/Project Modal */}
        {showCreatePropertyModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40">
          <div className="relative z-10 bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-w-md w-full mx-0 sm:mx-4 max-h-[90dvh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                  Create New {ownerType === 'customer' ? 'Property' : 'Project'}
                </h2>
                <button
                  onClick={() => {
                    setShowCreatePropertyModal(false)
                    setNewPropertyData({
                      name: '',
                      address: '',
                      city: '',
                      state: '',
                      zip: '',
                      property_type: '',
                      building_type: '',
                      unit_count: '',
                    })
                    setErrors({})
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {errors.property && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {errors.property}
                </div>
              )}
              {errors.project && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {errors.project}
                </div>
              )}

              <div className="space-y-4">
                {ownerType === 'customer' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Property Name *
                    </label>
                    <input
                      type="text"
                      value={newPropertyData.name}
                      onChange={(e) =>
                        setNewPropertyData({ ...newPropertyData, name: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter property name"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address {ownerType === 'lead' ? '*' : ''}
                  </label>
                  <AddressAutocomplete
                    value={newPropertyData.address}
                    onChange={(address) =>
                      setNewPropertyData({ ...newPropertyData, address })
                    }
                    onAddressSelect={(addressData) => {
                      setNewPropertyData({
                        ...newPropertyData,
                        address: addressData.address,
                        city: addressData.city,
                        state: addressData.state,
                        zip: addressData.zip,
                      })
                    }}
                    placeholder="Start typing address..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      value={newPropertyData.city}
                      onChange={(e) =>
                        setNewPropertyData({ ...newPropertyData, city: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="City"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      value={newPropertyData.state}
                      onChange={(e) =>
                        setNewPropertyData({ ...newPropertyData, state: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="State"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    value={newPropertyData.zip}
                    onChange={(e) =>
                      setNewPropertyData({ ...newPropertyData, zip: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="ZIP Code"
                  />
                </div>

                {ownerType === 'lead' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Building Type *
                      </label>
                      <select
                        value={newPropertyData.building_type}
                        onChange={(e) =>
                          setNewPropertyData({
                            ...newPropertyData,
                            building_type: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select type</option>
                        {BUILDING_TYPES.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Unit Count
                      </label>
                      <input
                        type="number"
                        value={newPropertyData.unit_count}
                        onChange={(e) =>
                          setNewPropertyData({
                            ...newPropertyData,
                            unit_count: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Number of units"
                        min={0}
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {ownerType === 'customer' ? 'Property Type' : 'Work Type'}
                  </label>
                  <select
                    value={newPropertyData.property_type}
                    onChange={(e) =>
                      setNewPropertyData({ ...newPropertyData, property_type: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">
                      {ownerType === 'customer' ? 'Select property type...' : 'Select work type...'}
                    </option>
                    {ownerType === 'customer' ? (
                      <>
                        <option value="residential">Residential</option>
                        <option value="commercial">Commercial</option>
                        <option value="multi_unit">Multi-Unit Building</option>
                        <option value="office">Office Building</option>
                        <option value="retail">Retail Space</option>
                        <option value="warehouse">Warehouse</option>
                      </>
                    ) : (
                      serviceCategories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreatePropertyModal(false)
                    setNewPropertyData({
                      name: '',
                      address: '',
                      city: '',
                      state: '',
                      zip: '',
                      property_type: '',
                      building_type: '',
                      unit_count: '',
                    })
                    setErrors({})
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={creatingProperty}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (ownerType === 'customer') {
                      handleCreateProperty()
                    } else {
                      handleCreateProject()
                    }
                  }}
                  disabled={creatingProperty}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingProperty ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
  )

  if (isFullPage) {
    return formContent
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      {formContent}
    </div>
  )
}

